import { Link } from 'react-router-dom';
import { FaArrowRight, FaBolt, FaBuilding, FaCheckCircle, FaLightbulb, FaWrench } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';

const copy = {
    en: {
        eyebrow: 'COMPLETE CHANNEL LETTER SERVICE',
        title: 'The Right Sign, From Design to Maintenance',
        subtitle: 'We help Houston businesses choose the right lighting style, build the sign, install it professionally and keep it working.',
        typesTitle: 'Channel Letter Options',
        types: [
            { icon: FaLightbulb, title: 'Front-Lit Letters', text: 'Bright acrylic faces for maximum storefront visibility during the day and at night.' },
            { icon: FaBuilding, title: 'Reverse-Lit / Halo', text: 'Light projects behind each letter to create a refined halo effect on the wall.' },
            { icon: FaBolt, title: 'Combination-Lit', text: 'Front illumination and a rear halo combined for a premium, high-impact appearance.' }
        ],
        processTitle: 'How Your Project Moves Forward',
        process: ['Consultation and site measurements', 'Custom design and approval', 'Professional fabrication', 'Installation and final inspection', 'Maintenance and repair when needed'],
        maintenanceTitle: 'Already Have a Sign?',
        maintenanceText: 'We also service existing channel letters, including LED and electrical troubleshooting, cleaning, face replacement and general repairs.',
        maintenanceCta: 'Request Maintenance',
        faqTitle: 'Channel Letter Questions',
        faqs: [
            ['Which type of channel letter is best for my business?', 'It depends on your brand, building surface, desired lighting effect and budget. We review those details before recommending an option.'],
            ['Can you repair an existing channel letter sign?', 'Yes. We can inspect lighting, electrical components, faces and mounting, then recommend repair or replacement where necessary.'],
            ['Do channel letters require permits?', 'Requirements vary by city, property and shopping center. We help identify and coordinate the permits or approvals that may be needed before installation.'],
            ['How long does a project take?', 'Fabrication typically takes 2–4 weeks after final design approval. The complete timeline depends on permits, materials, electrical work and site conditions.']
        ]
    },
    es: {
        eyebrow: 'SERVICIO COMPLETO DE CHANNEL LETTERS',
        title: 'El letrero correcto, desde el diseño hasta el mantenimiento',
        subtitle: 'Ayudamos a negocios de Houston a elegir la iluminación adecuada, fabricar su letrero, instalarlo profesionalmente y mantenerlo funcionando.',
        typesTitle: 'Opciones de Channel Letters',
        types: [
            { icon: FaLightbulb, title: 'Iluminación frontal', text: 'Caras de acrílico brillantes para máxima visibilidad de la fachada de día y de noche.' },
            { icon: FaBuilding, title: 'Reverse-lit / Halo', text: 'La luz se proyecta detrás de cada letra y crea un elegante efecto de halo en la pared.' },
            { icon: FaBolt, title: 'Iluminación combinada', text: 'Iluminación frontal y halo posterior para una apariencia prémium de gran impacto.' }
        ],
        processTitle: 'Cómo realizamos tu proyecto',
        process: ['Consulta y medidas del lugar', 'Diseño personalizado y aprobación', 'Fabricación profesional', 'Instalación e inspección final', 'Mantenimiento y reparación cuando sea necesario'],
        maintenanceTitle: '¿Ya tienes un letrero?',
        maintenanceText: 'También damos servicio a channel letters existentes: diagnóstico LED y eléctrico, limpieza, reemplazo de caras y reparaciones generales.',
        maintenanceCta: 'Solicitar mantenimiento',
        faqTitle: 'Preguntas sobre Channel Letters',
        faqs: [
            ['¿Qué tipo de channel letters conviene para mi negocio?', 'Depende de tu marca, la superficie del edificio, el efecto de iluminación y el presupuesto. Revisamos esos detalles antes de recomendar una opción.'],
            ['¿Pueden reparar un letrero existente?', 'Sí. Podemos revisar iluminación, componentes eléctricos, caras y montaje, y recomendar la reparación o sustitución necesaria.'],
            ['¿Los channel letters necesitan permisos?', 'Los requisitos varían según la ciudad, propiedad y centro comercial. Ayudamos a identificar y coordinar los permisos o aprobaciones que podrían necesitarse antes de instalar.'],
            ['¿Cuánto tarda un proyecto?', 'La fabricación normalmente toma de 2 a 4 semanas después de aprobar el diseño final. El plazo completo depende de permisos, materiales, trabajo eléctrico y condiciones del lugar.']
        ]
    }
};

const ChannelLetterGuide = () => {
    const { language } = useLanguage();
    const { isDark } = useTheme();
    const text = copy[language] || copy.en;

    return (
        <section id="channel-letter-guide" className={`py-20 px-4 ${isDark ? 'bg-gray-900' : 'bg-green-50'}`}>
            <div className="max-w-7xl mx-auto">
                <div className="text-center max-w-4xl mx-auto mb-14">
                    <p className="text-atlantis font-black tracking-widest text-sm mb-3">{text.eyebrow}</p>
                    <h2 className={`text-4xl md:text-5xl font-black mb-5 ${isDark ? 'text-white' : 'text-saratoga'}`}>{text.title}</h2>
                    <p className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{text.subtitle}</p>
                </div>

                <h3 className={`text-2xl font-black text-center mb-8 ${isDark ? 'text-white' : 'text-saratoga'}`}>{text.typesTitle}</h3>
                <div className="grid md:grid-cols-3 gap-6 mb-16">
                    {text.types.map(({ icon: Icon, title, text: description }) => (
                        <article key={title} className={`rounded-2xl p-7 border shadow-lg ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-green-200'}`}>
                            <div className="w-14 h-14 rounded-2xl bg-atlantis/15 flex items-center justify-center mb-5"><Icon className="text-atlantis text-2xl" /></div>
                            <h4 className={`text-xl font-black mb-3 ${isDark ? 'text-white' : 'text-saratoga'}`}>{title}</h4>
                            <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>{description}</p>
                        </article>
                    ))}
                </div>

                <div className="grid lg:grid-cols-2 gap-8 items-stretch mb-16">
                    <div className={`rounded-3xl p-8 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                        <h3 className={`text-2xl font-black mb-6 ${isDark ? 'text-white' : 'text-saratoga'}`}>{text.processTitle}</h3>
                        <ol className="space-y-4">
                            {text.process.map((step, index) => (
                                <li key={step} className={`flex items-center gap-4 font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                                    <span className="w-9 h-9 flex-none rounded-full bg-atlantis text-black font-black flex items-center justify-center">{index + 1}</span>{step}
                                </li>
                            ))}
                        </ol>
                    </div>
                    <div className="rounded-3xl p-8 bg-gradient-to-br from-atlantis to-green-yellow-primary text-black shadow-xl flex flex-col justify-center">
                        <FaWrench className="text-4xl mb-5" />
                        <h3 className="text-3xl font-black mb-4">{text.maintenanceTitle}</h3>
                        <p className="text-lg font-medium mb-7">{text.maintenanceText}</p>
                        <Link to="/contact" className="inline-flex self-start items-center gap-3 rounded-full bg-black text-white px-7 py-4 font-black hover:shadow-xl">
                            {text.maintenanceCta} <FaArrowRight />
                        </Link>
                    </div>
                </div>

                <div className="max-w-4xl mx-auto">
                    <h3 className={`text-3xl font-black text-center mb-8 ${isDark ? 'text-white' : 'text-saratoga'}`}>{text.faqTitle}</h3>
                    <div className="space-y-4">
                        {text.faqs.map(([question, answer]) => (
                            <details key={question} className={`group rounded-2xl border p-5 ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-green-200 text-saratoga'}`}>
                                <summary className="cursor-pointer list-none flex items-center gap-3 font-black text-lg">
                                    <FaCheckCircle className="text-atlantis flex-none" /> {question}
                                </summary>
                                <p className={`mt-4 pl-8 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{answer}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ChannelLetterGuide;
